import { lazy, useMemo } from 'react';

import { useToast } from '@chakra-ui/react';

import { isDefined, isNotEmpty } from '@enterprise/core';
import { DefaultLayout, FullScreenLayout, SseProvider } from '@enterprise/ui';

import type { Routes } from '@/app/types/routes';

import HeartbeatTimer from '@/app/components/heartbeat-timer/heartbeat-timer';
import Breadcrumbs from '@/app/components/layout/breadcrumbs';
import MainContent from '@/app/components/layout/main-content';
import NavBar from '@/app/components/layout/nav-bar';
import TopBar from '@/app/components/layout/top-bar';
import { RouteGuard } from '@/app/components/route-guard/route-guard';
import SessionRecovery from '@/app/components/session-recovery/session-recovery';
import { loginAction } from '@/app/pages/actions/login.action';
import Denied from '@/app/pages/denied';
import UnexpectedError from '@/app/pages/error';
import Login from '@/app/pages/login';
import Logout from '@/app/pages/logout';
import NotFound from '@/app/pages/not-found';
import Session from '@/app/pages/session';
import { getEnvVar } from '@/utilities/get-env-var';

import { datastreamRoutes } from './datastream.routes';

const NthCalculatorPage = lazy(() => {
  return import('@/app/pages/nth-calculator');
});

export const useAppRoutes = () => {
  const toast = useToast();

  const [enabled, uri] = useMemo(() => {
    const sseUri = getEnvVar('VITE_APP_ENTERPRISE_SERVER_SENT_EVENTS_URI');
    const isEnabled = isDefined(sseUri) && isNotEmpty(sseUri);

    return [isEnabled, sseUri];
  }, []);

  return useMemo<Routes>(() => {
    return {
      defaultLayout: {
        children: [
          {
            children: [
              // datastream routes
              datastreamRoutes,
              // TODO: add more routes here
            ],
            element: <SessionRecovery />,
          },
        ],
        element: (
          <DefaultLayout
            footer={getEnvVar('VITE_APP_ENVIRONMENT') === 'local' ? <HeartbeatTimer /> : undefined}
            header={
              <>
                <TopBar />
                <NavBar />
                <Breadcrumbs />
              </>
            }>
            <SseProvider
              enabled={enabled}
              onGlobalMessage={(e) => {
                toast({
                  description: `Message: ${e.data}`,
                  duration: 5000,
                  isClosable: true,
                  status: 'info',
                  title: 'Global Message Received',
                });
              }}
              uri={uri}>
              <MainContent />
            </SseProvider>
          </DefaultLayout>
        ),
        path: '/',
      },
      fullPageLayout: {
        children: [
          {
            element: <Denied />,
            path: 'denied',
          },
          {
            element: <UnexpectedError />,
            path: 'error',
          },
          {
            action: loginAction,
            element: <Login />,
            path: 'login',
          },
          {
            element: <Logout />,
            path: 'logout',
          },
          {
            element: <NotFound />,
            path: '404',
          },
          {
            element: <Session />,
            path: 'session',
          },
          {
            children: [
              {
                element: <NthCalculatorPage />,
                index: true,
              },
            ],
            element: <RouteGuard />,
            path: 'datastream/nth-calculator',
          },
        ],
        element: <FullScreenLayout />,
        path: '/',
      },
    };
  }, [enabled, toast, uri]);
};
